import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import PackageApplicantForm from './PackageApplicantForm'; 
import { toast } from 'react-toastify';
import { RiEdit2Line,RiUserSearchLine, RiDeleteBin6Line, RiArrowDownSLine, RiArrowUpSLine, RiUserAddLine, RiTeamLine } from 'react-icons/ri';
import DeleteAlert from '../../Common/Layout/DeleteAlert'; 
import './PackageManageApplicants.css'; 

const PackageManageApplicants = ({ packageId }) => {
    const [applicants, setApplicants] = useState([]);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedDetails, setExpandedDetails] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [recordForEdit, setRecordForEdit] = useState(null);
    const [parentApplicantId, setParentApplicantId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const GetData = useCallback(async () => {
        if (!packageId) return;
        setLoading(true);
        try {
            const response = await apiService.getWithParams(`${config.GETAPPLICANTSBYPACKAGE}/${packageId}`, { pageNumber: 1, pageSize: 100 });
            console.log(response)
            const allApplicants = response.data || [];
            setApplicants(allApplicants);

            if (allApplicants.length > 0) {
                const currentSelectedExists = allApplicants.some(a => a.packageApplicantId === selectedApplicant?.packageApplicantId);
                if (!currentSelectedExists) {
                    const firstMainApplicant = allApplicants.find(a => !a.parentApplicantId);
                    setSelectedApplicant(firstMainApplicant || allApplicants[0]);
                }
            } else {
                setSelectedApplicant(null);
            }
        } catch (error) {
            console.error("Error fetching applicants:", error);
        } finally {
            setLoading(false);
        }
    }, [packageId, selectedApplicant]);

    useEffect(() => {
        GetData();
    }, [packageId]); 

    const handleFormSuccess = () => {
        setShowForm(false);
        setRecordForEdit(null);
        setParentApplicantId(null);
        GetData(); 
    };

    const showEditDetails = (applicantToEdit) => {
        setRecordForEdit(applicantToEdit);
        setParentApplicantId(applicantToEdit.parentApplicantId);
        setShowForm(true);
    };
    
    const handleAddMainApplicant = () => {
        setRecordForEdit(null);
        setParentApplicantId(null);
        setShowForm(true);
    };

    const handleAddFamilyMember = () => {
        if (!selectedApplicant) return;
        setRecordForEdit(null);
        setParentApplicantId(selectedApplicant.packageApplicantId);
        setShowForm(true);
    };

    const handleOpenDeleteModal = (applicant) => {
        setItemToDelete(applicant);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };
    
    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await apiService.remove(`${config.DELETEAPPLICANT}/${itemToDelete.packageApplicantId}`);
            await GetData();
        } catch (error) {
            console.error("Error deleting applicant:", error);
        }
        handleCloseDeleteModal();
    };
    
    const toggleDetails = (id) => {
        setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const mainApplicants = applicants.filter(a => !a.parentApplicantId);
    const familyMembersOfSelected = selectedApplicant 
        ? applicants.filter(a => a.parentApplicantId === selectedApplicant.packageApplicantId) 
        : [];

    if (showForm) {
        return (
            <PackageApplicantForm
                packageId={packageId}
                applicantForEdit={recordForEdit} 
                parentApplicantId={parentApplicantId}
                onSuccess={handleFormSuccess}
                onCancel={() => setShowForm(false)}
            />
        );
    }
   
    const renderApplicantDetails = (applicant) => (
        <div className="card shadow-sm">
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-4 text-center">
                        <h6>Profile Photo</h6>
                        <img src={applicant.profileSrc || '/assets/images/avatar.png'} alt="Profile" className="img-thumbnail" style={{ height: '150px' }}/>
                    </div>
                    <div className="col-md-4 text-center">
                        <h6>Passport Front</h6>
                         <img src={applicant.passportFrontSrc || '/assets/images/doc_bgc.png'} alt="Passport Front" className="img-thumbnail" style={{ height: '150px' }}/>
                    </div>
                    <div className="col-md-4 text-center">
                        <h6>Passport Back</h6>
                         <img src={applicant.passportBackSrc || '/assets/images/doc_bgc.png'} alt="Passport Back" className="img-thumbnail" style={{ height: '150px' }}/>
                    </div>
                </div>
                <table className="table table-bordered table-sm">
                    <tbody>
                        <tr><th>Name</th><td>{applicant.firstName} {applicant.lastName}</td></tr>
                        <tr><th>Passport No.</th><td>{applicant.passportNumber}</td></tr>
                        <tr><th>Traveler Type</th><td>{applicant.travelerType}</td></tr>
                        <tr><th>Email</th><td>{applicant.contactEmail}</td></tr>
                         <tr><th>Phone Number</th><td>{applicant.contactPhoneNumber}</td></tr>
                        <tr><th>Status</th><td><span className="badge bg-info">{applicant.packageApplicantStatus}</span></td></tr>
                    </tbody>
                </table>
                 <div className="mt-3">
                    <h6>Additional Documents</h6>
                    {applicant.additionalDocuments?.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                            {applicant.additionalDocuments.map(doc => (
                                <a key={doc.applicantDocumentId} href={doc.imageSrc} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                                    <i className="ri-file-text-line me-1"></i> {doc.imageName.substring(0, 20)}...
                                </a>
                            ))}
                        </div>
                    ) : <p className="text-muted">No additional documents.</p>}
                </div>
            </div>
        </div>
    );

        return (
        <div className="applicant-manager-wrapper">
            <div className="page-header">
                <button className="btn btn-primary" onClick={handleAddMainApplicant}>
                    <RiUserAddLine className='me-1' /> Add Main Applicant
                </button>
            </div>
            
            {loading ? (
                <div className="text-center p-5"><div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}></div></div>
            ) : applicants.length === 0 ? (
                <div className="empty-state-container">
                    <div className="empty-state-icon"><RiUserSearchLine /></div>
                    <h3 className="empty-state-title">No Applicants Added Yet</h3>
                    <p className="empty-state-text">
                        Click the "Add Main Applicant" button above to get started.
                    </p>
                </div>
            ) : (
                <div className="row">
                    {/* --- REFINED LEFT COLUMN --- */}
                    <div className="col-lg-5 col-xl-4 applicant-list-column">
                        <h5 className="section-title">Applicants List</h5>
                        <div className="card applicant-list-card">
                            <div className="list-group list-group-flush">
                                {mainApplicants.map((app, index) => {
                                    const familyCount = applicants.filter(a => a.parentApplicantId === app.packageApplicantId).length;
                                    return (
                                        <a href="#!" key={app.packageApplicantId} 
                                             className={`list-group-item list-group-item-action ${selectedApplicant?.packageApplicantId === app.packageApplicantId ? 'active' : ''}`}
                                             onClick={(e) => { e.preventDefault(); setSelectedApplicant(app); }}>
                                            <div className="d-flex w-100 justify-content-between">
                                               <h5 className="mb-1">{index + 1}. {app.firstName} {app.lastName}</h5>
                                                {familyCount > 0 && <span className="badge bg-primary rounded-pill">{familyCount} Family</span>}
                                            </div>
                                            <p className="mb-1 small text-muted">Passport: {app.passportNumber} | Type: {app.travelerType}</p>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7 col-xl-8 applicant-details-column">
                         {selectedApplicant ? (
                             <div>
                                <div className="applicant-card" onClick={() => toggleDetails(selectedApplicant.packageApplicantId)} style={{cursor: 'pointer'}}>
                                    <h6>Main Applicant: {selectedApplicant.firstName} {selectedApplicant.lastName}</h6>
                                    <div className="applicant-actions">
                                        <button title="Edit" className="btn-action" onClick={(e) => { e.stopPropagation(); showEditDetails(selectedApplicant); }}><RiEdit2Line /></button>
                                        <button title="Delete" className="btn-action delete" onClick={(e) => { e.stopPropagation(); handleOpenDeleteModal(selectedApplicant); }}><RiDeleteBin6Line /></button>
                                        {expandedDetails[selectedApplicant.packageApplicantId] ? <RiArrowUpSLine size={20}/> : <RiArrowDownSLine size={20}/>}
                                    </div>
                                </div>
                                {expandedDetails[selectedApplicant.packageApplicantId] && renderApplicantDetails(selectedApplicant)}
                                <div className="section-header mt-4">
                                    <h5 className="section-title">Group Members</h5>
                                    <button className="btn btn-success" onClick={handleAddFamilyMember}>
                                        <RiTeamLine className='me-1'/> Add Group Member
                                    </button>
                                </div>
                                
                                {familyMembersOfSelected.length > 0 ? familyMembersOfSelected.map(member => (
                                     <React.Fragment key={member.packageApplicantId}>
                                        <div className="applicant-card" onClick={() => toggleDetails(member.packageApplicantId)} style={{cursor: 'pointer'}}>
                                            <h6>{member.firstName} {member.lastName}</h6>
                                            <div className="applicant-actions">
                                                <button title="Edit" className="btn-action" onClick={(e) => { e.stopPropagation(); showEditDetails(member); }}><RiEdit2Line /></button>
                                                <button title="Delete" className="btn-action delete" onClick={(e) => { e.stopPropagation(); handleOpenDeleteModal(member); }}><RiDeleteBin6Line /></button>
                                                {expandedDetails[member.packageApplicantId] ? <RiArrowUpSLine size={20}/> : <RiArrowDownSLine size={20}/>}
                                            </div>
                                        </div>
                                        {/* THIS IS THE MISSING LINE! It conditionally renders the details for the group member. */}
                                        {expandedDetails[member.packageApplicantId] && renderApplicantDetails(member)}
                                    </React.Fragment>
                                    
                                )) : 
                                <div className="text-center p-4 border rounded bg-light">
                                    <p className="text-muted mb-0">No family members have been added for this applicant.</p>
                                </div>
                                }
                             </div>
                         ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 text-center border rounded bg-light">
                                <div>
                                    <p className="text-muted">Select an applicant from the list to view their details.</p>
                                </div>
                            </div>
                         )}
                    </div>
                </div>
            )}

            <DeleteAlert
                show={showDeleteModal}
                handleClose={handleCloseDeleteModal}
                handleDelete={handleDelete}
            />
        </div>
    );
};

export default PackageManageApplicants;
